import {changeMaxPage} from 'Agent/Accounts/ducks/account';
import {Page} from 'Core/models/Page';
import React, {CSSProperties, ReactNode, useContext, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import AutoSizer from 'react-virtualized-auto-sizer';
import {FixedSizeList as List} from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import {ReducerState} from 'rootReducer';
import {Column} from 'Shared/components/PagedView/PagedView';
import {ColumnWidthsContext, ROW_SIZE} from 'Shared/components/PagedView/Table/Table';
import LoadingRow from 'Shared/components/PagedView/Table/TableLoadingRow';
import 'Shared/components/Table/Table.sass';
import PagedViewLoadNext, {PagedViewLoadNextProps} from 'Shared/ducks/PagedView';
import {toKebabCase} from 'Shared/utilities/stringFormatHelpers';
import {getFeatureToggleVariable} from '../../../../feature';
import PagedViewSnackBar from '../PageLimitSnackbar';

type TableBodyProps<TData> = {
  data: Page<TData>;
  loadMore: (page: Page<TData>) => void;
  setHeight: (height: number) => void;
  setWidth: (width: number) => void;
  columns: Array<Column<TData>>;
  pagedViewKey: string;
};

const getItemCountWithSpaceForLoading = (totalCount: number, currentResultCount: number) =>
  totalCount === currentResultCount ? currentResultCount : currentResultCount + 1;

const TableBody = <TData extends {}>(props: TableBodyProps<TData>) => {
  const {columns, loadMore, data, setHeight, setWidth, pagedViewKey} = props;
  const {maxPage} = useSelector((state: ReducerState) => state.account);
  const dispatch = useDispatch();
  const [maxOpen, setMaxOpen] = useState(true);
  const [warningOpen, setWarningOpen] = useState(true);

  const onCloseMax = () => {
    setMaxOpen(false);
  };
  const onCloseWarning = () => {
    setWarningOpen(false);
  };

  const {PAGE_LIMIT, RECORDS_PER_PAGE} = getFeatureToggleVariable();

  const TableRowInfo = (index: number, style: CSSProperties) => (
    <TableRow key={`table-row__${index}`} columns={columns} data={data} index={index} style={style} />
  );

  if (!data || !data.result || !data.totalCount) {
    return <div style={{marginLeft: '45%', marginTop: '5%'}}>No data found</div>;
  }

  return (
    <AutoSizer
      onResize={({height, width}) => {
        setHeight(height);
        setWidth(width);
      }}
    >
      {({height, width}) => (
        <InfiniteLoader
          isItemLoaded={index => Boolean(data.result[index])}
          threshold={3}
          itemCount={data.totalCount + 1}
          loadMoreItems={(start, stop) => {
            const infoPagedViewLoadNext: PagedViewLoadNextProps<TData> = {
              data: data,
              stop: stop,
              start: start,
              pagedViewKey: pagedViewKey,
              loadMore: loadMore,
              RECORDS_PER_PAGE: RECORDS_PER_PAGE,
              PAGE_LIMIT: PAGE_LIMIT,
              dispatch: dispatch,
              changeMaxPage: changeMaxPage,
            };
            PagedViewLoadNext(infoPagedViewLoadNext);
          }}
        >
          {({onItemsRendered, ref}) => (
            <List
              onItemsRendered={onItemsRendered}
              ref={ref}
              height={height}
              width={width}
              overscanCount={5}
              itemCount={getItemCountWithSpaceForLoading(data.totalCount, data.result.length)}
              itemSize={ROW_SIZE}
            >
              {({index, style}) =>
                pagedViewKey != 'accounts' ? (
                  <TableRow key={`table-row__${index}`} columns={columns} data={data} index={index} style={style} />
                ) : (
                  <PagedViewSnackBar
                    index={index}
                    style={style}
                    maxPage={maxPage}
                    maxOpen={maxOpen}
                    RECORDS_PER_PAGE={RECORDS_PER_PAGE}
                    PAGE_LIMIT={PAGE_LIMIT}
                    onCloseMax={onCloseMax}
                    onCloseWarning={onCloseWarning}
                    warningOpen={warningOpen}
                    infoRow={TableRowInfo(index, style)}
                    isMobile={false}
                  />
                )
              }
            </List>
          )}
        </InfiniteLoader>
      )}
    </AutoSizer>
  );
};

type TableRowProps<TData> = {
  style: CSSProperties;
  columns: Array<Column<TData>>;
  index: number;
  data: Page<TData>;
};

const TableRow = <TData extends {}>(props: TableRowProps<TData>) => {
  const {style, columns, index, data} = props;
  const isLoadingRow = (index: number) => index === data.result.length;

  return isLoadingRow(index) ? (
    <LoadingRow style={style} />
  ) : (
    <div style={style} className="paged-table__row" role="row" aria-busy="false">
      {columns.map(col => (
        <TableCell
          id={`table-row-${toKebabCase(col.title)}-${index}`}
          title={col.title}
          key={`cell_${toKebabCase(col.title)}_${index}`}
          value={col.getContent(data.result[index])}
          dataprivacy={col?.ddprivacy}
        />
      ))}
    </div>
  );
};

const TableCell = (props: {id: string; title: string; value: ReactNode; dataprivacy?: string}) => {
  const {id, title, value, dataprivacy} = props;
  const columnWidths = useContext(ColumnWidthsContext);
  const width = columnWidths.get(title);

  return (
    <div
      id={id}
      style={{width: `${width}%`}}
      className="paged-table__cell"
      data-dd-privacy={dataprivacy ? dataprivacy : 'allow'}
      role="cell"
    >
      {value}
    </div>
  );
};

export default TableBody;
